import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { LOCAL_STORAGE, UserStorageService } from './user-storage.service';

export type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'silent';

/**
 * Lightweight logging service with namespace-based opt-in filtering.
 *
 * Key design: uses console.bind() so Chrome DevTools correctly shows the
 * original call-site file/line instead of this service.
 *
 * All namespaces default to **silent**. Enable a namespace in the Logging
 * settings page or manually:
 *   localStorage.setItem('cp:log:myNamespace', 'debug');
 *
 * Streaming to the backend is still supported. When streaming is active the
 * logger switches to wrapper functions so it can capture + forward entries
 * (source-line attribution is lost during streaming, which is fine because
 * you're reading the streamed output on the server).
 *
 * Usage:
 * ```typescript
 * const log = inject(LoggerService).getLogger('my-namespace');
 * log.debug('Detailed debug info');
 * log.info('General info');
 * log.warn('Warning message');
 * log.error('Error occurred', error);
 * ```
 */

/** The object returned by getLogger() */
export interface NamespaceLogger {
  trace: (...args: any[]) => void;
  debug: (...args: any[]) => void;
  info: (...args: any[]) => void;
  warn: (...args: any[]) => void;
  error: (...args: any[]) => void;
  log: (...args: any[]) => void;
  /** Current effective level for this namespace */
  getLevel: () => LogLevel;
  /** Override the level for this namespace (persisted) */
  setLevel: (level: LogLevel) => void;
}

// Numeric ordering for level gating
const LEVEL_ORDER: Record<LogLevel, number> = {
  trace: 0,
  debug: 1,
  info: 2,
  warn: 3,
  error: 4,
  silent: 5,
};

// Map our level names to actual console methods
const CONSOLE_METHOD: Record<string, 'log' | 'debug' | 'info' | 'warn' | 'error'> = {
  trace: 'debug',
  debug: 'debug',
  info: 'info',
  warn: 'warn',
  error: 'error',
  log: 'log',
};

/** localStorage key prefix for per-namespace log level */
const LEVEL_KEY_PREFIX = 'cp:log:';

interface StreamEntry {
  timestamp: string;
  level: LogLevel;
  namespace: string;
  args: any[];
}

interface SyncObservabilityMetadata {
  verify_batch_failures: number;
  verify_checksum_mismatches: number;
  table_digest_mismatches: number;
  forced_full_resync_count: number;
  last_integrity_reason: string | null;
  last_integrity_at: string | null;
}

/** Default to debug on localhost so devs see logs without config */
const isLocalhost =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

@Injectable({ providedIn: 'root' })
export class LoggerService {
  private loggers = new Map<string, NamespaceLogger>();
  private DEFAULT_LEVEL: LogLevel = isLocalhost ? 'debug' : 'silent';
  private userStorage = inject(UserStorageService);
  private localStorage = inject(LOCAL_STORAGE);

  // Stream logs to backend: stores expiry timestamp in global localStorage
  private readonly STREAM_LOGS_KEY = 'stream-logs-expires-at';
  private readonly STREAM_LOGS_DURATION_MS = 60 * 60 * 1000; // 1 hour
  // Opt-in: include tab ID in the backend log prefix (useful when debugging multi-tab issues)
  private readonly SHOW_TAB_ID_KEY = 'stream-logs-show-tab-id';

  // Queue of pending log entries waiting to be streamed to backend
  private streamQueue: StreamEntry[] = [];
  private streamDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly STREAM_DEBOUNCE_MS = 500;
  /** Cap the retry queue so a permanently-unreachable backend doesn't grow memory unboundedly */
  private readonly MAX_STREAM_QUEUE_SIZE = 200;

  // Ring buffer: always captures warn+error regardless of streaming state
  // so sendLogsToBackend() has retrospective context even without prior streaming.
  private readonly RING_BUFFER_SIZE = 500;
  private ringBuffer: StreamEntry[] = [];

  /**
   * Internal logger for messages originating within LoggerService itself.
   * Uses getLogger() so these messages respect level filtering and the ring
   * buffer — but they still appear as 'logger.service' in DevTools because
   * Angular's DI wires them back to this class.  That's expected.
   *
   * NOT used inside postLogsToBackend() to avoid a streaming feedback loop
   * (a failed HTTP flush would enqueue → flush → fail → enqueue …).
   */
  private get selfLog() {
    return this.getLogger('logger-service');
  }

  constructor() {
    this.loadConfig();
  }

  // ─── Public API ──────────────────────────────────────────────────────────

  /**
   * Get (or create) a logger for the given namespace.
   * The returned object has .trace/.debug/.info/.warn/.error/.log methods.
   * Chrome DevTools will correctly show the caller's file/line.
   */
  getLogger(namespace: string): NamespaceLogger {
    let logger = this.loggers.get(namespace);
    if (!logger) {
      logger = this.buildLogger(namespace);
      this.loggers.set(namespace, logger);
    }
    return logger;
  }

  /**
   * Set log level for a namespace. Persisted to localStorage.
   */
  setLevel(namespace: string, level: LogLevel): void {
    this.userStorage.setGlobalItem(`${LEVEL_KEY_PREFIX}${namespace}`, level);
    // Rebuild the logger so bind targets update
    this.rebuildLogger(namespace);
  }

  /**
   * Get current log level for a namespace.
   */
  getLevel(namespace: string): LogLevel {
    return this.resolveLevel(namespace);
  }

  /**
   * Set all listed namespaces to the same level.
   */
  setAllLevels(level: LogLevel, namespaces: string[]): void {
    namespaces.forEach((ns) => this.setLevel(ns, level));
  }

  /**
   * Get all registered (in-memory) namespace names.
   */
  getAllNamespaces(): string[] {
    return Array.from(this.loggers.keys()).sort();
  }

  /**
   * Get all namespaces that have an explicit level persisted in localStorage.
   */
  getConfiguredNamespaces(): string[] {
    return this.userStorage
      .getAllGlobalKeys()
      .filter((key) => key.startsWith(LEVEL_KEY_PREFIX))
      .map((key) => key.slice(LEVEL_KEY_PREFIX.length))
      .sort();
  }

  /**
   * Reset all logging configuration to defaults.
   */
  resetConfig(_namespaces: string[]): void {
    const keysToRemove = this.userStorage
      .getAllGlobalKeys()
      .filter((key) => key.startsWith(LEVEL_KEY_PREFIX));
    keysToRemove.forEach((key) => this.userStorage.removeGlobalItem(key));
    // Rebuild all loggers to pick up the default level
    for (const ns of this.loggers.keys()) {
      this.rebuildLogger(ns);
    }
  }

  /**
   * Get the global default log level.
   */
  getDefaultLevel(): LogLevel {
    return this.DEFAULT_LEVEL;
  }

  /**
   * Set the global default log level (persisted).
   */
  setDefaultLevel(level: LogLevel): void {
    this.DEFAULT_LEVEL = level;
    this.userStorage.setGlobalItem(`${LEVEL_KEY_PREFIX}default`, level);
    // Rebuild loggers that don't have an explicit level (they inherit default)
    for (const ns of this.loggers.keys()) {
      if (!this.userStorage.getGlobalItem(`${LEVEL_KEY_PREFIX}${ns}`)) {
        this.rebuildLogger(ns);
      }
    }
  }

  // ─── Streaming to backend ─────────────────────────────────────────────

  /**
   * Whether streaming logs to the backend is currently active.
   */
  isStreamLogsEnabled(): boolean {
    const expiry = this.userStorage.getGlobalItem(this.STREAM_LOGS_KEY);
    if (!expiry) return false;
    return Date.now() < parseInt(expiry, 10);
  }

  /**
   * Enable or disable continuous log streaming to the backend.
   * Enabling sets a 1-hour auto-expiry. All loggers are rebuilt so they
   * switch between bind (normal) and wrapper (streaming) mode.
   */
  setStreamLogsEnabled(enabled: boolean): void {
    if (enabled) {
      const expiresAt = Date.now() + this.STREAM_LOGS_DURATION_MS;
      this.userStorage.setGlobalItem(this.STREAM_LOGS_KEY, String(expiresAt));
      // Use selfLog.info BEFORE rebuilding loggers so this message still goes
      // through the pre-streaming (bind) path and avoids an immediate loop.
      this.selfLog.info(`Log streaming enabled until ${new Date(expiresAt).toLocaleTimeString()}`);
    } else {
      this.userStorage.removeGlobalItem(this.STREAM_LOGS_KEY);
      if (this.streamQueue.length > 0) {
        this.flushStreamQueue();
      }
      this.selfLog.info('Log streaming disabled');
    }
    // Rebuild all loggers to switch between bind/wrapper mode
    for (const ns of this.loggers.keys()) {
      this.rebuildLogger(ns);
    }
  }

  /**
   * Whether the tab ID is included in the backend log prefix.
   * Off by default — only useful when debugging multi-tab scenarios.
   */
  isShowTabIdEnabled(): boolean {
    return this.userStorage.getGlobalItem(this.SHOW_TAB_ID_KEY) === 'true';
  }

  /**
   * Enable or disable the tab ID prefix in streamed backend logs.
   */
  setShowTabIdEnabled(enabled: boolean): void {
    if (enabled) {
      this.userStorage.setGlobalItem(this.SHOW_TAB_ID_KEY, 'true');
    } else {
      this.userStorage.removeGlobalItem(this.SHOW_TAB_ID_KEY);
    }
  }

  /**
   * Returns the Date when stream logging will auto-expire, or null.
   */
  getStreamLogsExpiresAt(): Date | null {
    if (!this.isStreamLogsEnabled()) return null;
    const expiry = this.userStorage.getGlobalItem(this.STREAM_LOGS_KEY);
    return expiry ? new Date(parseInt(expiry, 10)) : null;
  }

  /**
   * One-shot: send buffered warn/error entries to the backend.
   * The ring buffer retains the last RING_BUFFER_SIZE warn+error log entries
   * regardless of streaming state, so recent errors are always available.
   */
  async sendLogsToBackend(): Promise<boolean> {
    try {
      const entries = this.ringBuffer.slice(); // snapshot
      if (entries.length === 0) {
        const message = `[LoggerService] sendLogsToBackend at ${new Date().toISOString()} — ring buffer is empty (no warn/error entries recorded)`;
        return await this.postLogsToBackend(message);
      }
      const logsText = entries.map((e) => this.formatStreamEntry(e)).join('\n');
      return await this.postLogsToBackend(logsText);
    } catch (error) {
      this.selfLog.error('Failed to send logs to backend:', error);
      return false;
    }
  }

  /**
   * Export the ring buffer as a formatted string.
   * Contains the last RING_BUFFER_SIZE warn/error entries.
   */
  exportLogs(): string {
    if (this.ringBuffer.length === 0) {
      return '(ring buffer empty — no warn/error entries recorded yet)';
    }
    return this.ringBuffer.map((e) => this.formatStreamEntry(e)).join('\n');
  }

  // ─── Private helpers ──────────────────────────────────────────────────

  /**
   * Load persisted default level from localStorage.
   */
  private loadConfig(): void {
    const savedDefault = this.userStorage.getGlobalItem(`${LEVEL_KEY_PREFIX}default`);
    if (savedDefault) {
      this.DEFAULT_LEVEL = savedDefault as LogLevel;
    }
  }

  /**
   * Resolve effective level for a namespace: explicit > default.
   */
  private resolveLevel(namespace: string): LogLevel {
    const explicit = this.userStorage.getGlobalItem(`${LEVEL_KEY_PREFIX}${namespace}`);
    if (explicit && explicit in LEVEL_ORDER) {
      return explicit as LogLevel;
    }
    return this.DEFAULT_LEVEL;
  }

  /**
   * Build (or rebuild) a NamespaceLogger.
   *
   * When streaming is OFF: methods are created via console.bind() so the
   * browser devtools show the correct caller file/line.
   *
   * When streaming is ON: methods are wrapper functions that capture the
   * log entry for the stream queue AND call through to console.
   */
  private buildLogger(namespace: string): NamespaceLogger {
    const level = this.resolveLevel(namespace);
    const color = this.getNamespaceColor(namespace);
    const streaming = this.isStreamLogsEnabled();

    const methods = {} as Record<string, (...args: any[]) => void>;

    const methodNames = ['trace', 'debug', 'info', 'warn', 'error', 'log'] as const;

    for (const methodName of methodNames) {
      const methodLevel = methodName === 'log' ? 'debug' : methodName;
      const suppressed = LEVEL_ORDER[methodLevel] < LEVEL_ORDER[level];
      const consoleFn = CONSOLE_METHOD[methodName];

      const isWarnOrError = methodLevel === 'warn' || methodLevel === 'error';

      if (suppressed) {
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        methods[methodName] = function noop() {};
      } else if (streaming) {
        // Wrapper mode: capture to ring buffer + stream queue, then call console
        methods[methodName] = (...args: any[]) => {
          this.captureToRingBuffer(namespace, methodLevel as LogLevel, args);
          this.enqueueStreamEntry(namespace, methodLevel as LogLevel, args);
          window.console[consoleFn](
            `%c[${namespace}]%c`,
            `color: ${color}; font-weight: bold`,
            '',
            ...args,
          );
        };
      } else if (isWarnOrError) {
        // Warn/error always go through a wrapper so they land in the ring buffer,
        // making them available for sendLogsToBackend() even without prior streaming.
        methods[methodName] = (...args: any[]) => {
          this.captureToRingBuffer(namespace, methodLevel as LogLevel, args);
          window.console[consoleFn](
            `%c[${namespace}]%c`,
            `color: ${color}; font-weight: bold`,
            '',
            ...args,
          );
        };
      } else {
        // Bind mode: correct source-line attribution in DevTools
        methods[methodName] = window.console[consoleFn].bind(
          window.console,
          `%c[${namespace}]%c`,
          `color: ${color}; font-weight: bold`,
          '',
        );
      }
    }

    const logger: NamespaceLogger = {
      trace: methods['trace'],
      debug: methods['debug'],
      info: methods['info'],
      warn: methods['warn'],
      error: methods['error'],
      log: methods['log'],
      getLevel: () => this.resolveLevel(namespace),
      setLevel: (l: LogLevel) => this.setLevel(namespace, l),
    };

    return logger;
  }

  /**
   * Rebuild a logger in-place so existing references pick up new level / mode.
   */
  private rebuildLogger(namespace: string): void {
    const existing = this.loggers.get(namespace);
    if (!existing) return;
    const rebuilt = this.buildLogger(namespace);
    // Mutate in place so all holders of the reference see updated methods
    existing.trace = rebuilt.trace;
    existing.debug = rebuilt.debug;
    existing.info = rebuilt.info;
    existing.warn = rebuilt.warn;
    existing.error = rebuilt.error;
    existing.log = rebuilt.log;
    existing.getLevel = rebuilt.getLevel;
    existing.setLevel = rebuilt.setLevel;
  }

  /**
   * Get a consistent HSL colour for a namespace (deterministic hash).
   */
  private getNamespaceColor(namespace: string): string {
    let hash = 0;
    for (let i = 0; i < namespace.length; i++) {
      hash = namespace.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = hash % 360;
    const saturation = 65 + (hash % 20);
    const lightness = 45 + (hash % 15);
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  }

  // ─── Ring buffer ─────────────────────────────────────────────────────

  private captureToRingBuffer(namespace: string, level: LogLevel, args: any[]): void {
    if (this.ringBuffer.length >= this.RING_BUFFER_SIZE) {
      this.ringBuffer.shift();
    }
    this.ringBuffer.push({
      timestamp: new Date().toISOString(),
      level,
      namespace,
      args,
    });
  }

  // ─── Stream internals ────────────────────────────────────────────────

  private enqueueStreamEntry(namespace: string, level: LogLevel, args: any[]): void {
    this.streamQueue.push({
      timestamp: new Date().toISOString(),
      level,
      namespace,
      args,
    });
    if (this.streamDebounceTimer !== null) {
      clearTimeout(this.streamDebounceTimer);
    }
    this.streamDebounceTimer = setTimeout(() => {
      this.streamDebounceTimer = null;
      this.flushStreamQueue();
    }, this.STREAM_DEBOUNCE_MS);
  }

  private async flushStreamQueue(): Promise<void> {
    if (this.streamQueue.length === 0) return;
    const entries = this.streamQueue.splice(0, this.streamQueue.length);
    const logsText = entries.map((e) => this.formatStreamEntry(e)).join('\n');
    const success = await this.postLogsToBackend(logsText).catch(() => false);
    if (!success) {
      // Re-queue failed entries at the front so they're included in the next flush.
      // Cap total queue size to avoid unbounded growth if the backend is persistently unreachable.
      const combined = [...entries, ...this.streamQueue];
      this.streamQueue = combined.slice(0, this.MAX_STREAM_QUEUE_SIZE);
    }
  }

  private formatStreamEntry(entry: StreamEntry): string {
    const user = this.userStorage.getCurrentUserEmail();
    const argsStr = entry.args
      .map((arg) => {
        if (typeof arg === 'object') {
          try {
            return JSON.stringify(arg);
          } catch {
            return String(arg);
          }
        }
        return String(arg);
      })
      .join(' ');
    return `[${user}] [${entry.timestamp}] ${entry.level.toUpperCase()} [${entry.namespace}]: ${argsStr}`;
  }

  private async postLogsToBackend(logs: string): Promise<boolean> {
    try {
      const token = this.localStorage.getItem('auth_token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const tabId = this.isShowTabIdEnabled()
        ? (sessionStorage.getItem('checkpoint-tab-id') ?? 'unknown-tab')
        : undefined;
      const syncObservability = this.collectSyncObservabilityMetadata();

      const response = await fetch(`${environment.apiUrl}/logs/client`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ logs, tabId, syncObservability }),
        keepalive: true,
      });

      if (response.ok) {
        // Use window.console directly here (not selfLog) to avoid a streaming
        // feedback loop: selfLog in streaming mode would enqueue → flush → POST
        // → this success log → enqueue again … indefinitely.
        window.console.debug('[logger-service] ✅ Logs sent to backend successfully');
      } else {
        window.console.error(
          '[logger-service] ❌ Failed to send logs to backend:',
          response.status,
          response.statusText,
        );
      }
      return response.ok;
    } catch (error) {
      // TypeError: Failed to fetch is a transient network blip (e.g. during SW refresh).
      // Don't pollute the console — entries will be re-queued and retried on the next flush.
      // Also intentionally avoids selfLog to prevent streaming feedback loop.
      if (!(error instanceof TypeError && error.message === 'Failed to fetch')) {
        window.console.error('[logger-service] Failed to send logs to backend:', error);
      }
      return false;
    }
  }

  private collectSyncObservabilityMetadata(): SyncObservabilityMetadata {
    const getCounter = (key: string): number => {
      const raw = this.localStorage.getItem(key);
      const parsed = raw ? parseInt(raw, 10) : 0;
      return Number.isFinite(parsed) ? parsed : 0;
    };

    return {
      verify_batch_failures: getCounter('cp:sync:obs:verify_batch_failures'),
      verify_checksum_mismatches: getCounter('cp:sync:obs:verify_checksum_mismatches'),
      table_digest_mismatches: getCounter('cp:sync:obs:table_digest_mismatches'),
      forced_full_resync_count: getCounter('cp:sync:obs:forced_full_resync_count'),
      last_integrity_reason: this.localStorage.getItem('cp:sync:obs:last_integrity_reason'),
      last_integrity_at: this.localStorage.getItem('cp:sync:obs:last_integrity_at'),
    };
  }
}
